/**
 * First-party consent gating (doc 116 §7 D5).
 * Embed stays inert until companion consent AND any site consent gate pass.
 */

export const COMPANION_CONSENT_STORAGE_KEY = 'camlin-companion-consent-v1';

export type CompanionConsentChoice = 'accepted' | 'declined';

export interface CompanionConsentRecord {
  choice: CompanionConsentChoice;
  recordedAt: string;
}

export interface CompanionConsentStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CompanionSiteConsentGate {
  /** Returns true when the host site's own consent layer allows Companion to activate. */
  isOpen(): boolean;
}

declare global {
  interface Window {
    __camlinCompanionSiteConsent?: CompanionSiteConsentGate;
  }
}

export function readCompanionConsent(storage: CompanionConsentStorage): CompanionConsentRecord | null {
  const raw = storage.getItem(COMPANION_CONSENT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CompanionConsentRecord;
    if (parsed.choice === 'accepted' || parsed.choice === 'declined') {
      return parsed;
    }
  } catch {
    // ignore corrupt storage
  }

  return null;
}

export function writeCompanionConsent(
  storage: CompanionConsentStorage,
  choice: CompanionConsentChoice
): CompanionConsentRecord {
  const record: CompanionConsentRecord = {
    choice,
    recordedAt: new Date().toISOString()
  };
  storage.setItem(COMPANION_CONSENT_STORAGE_KEY, JSON.stringify(record));
  return record;
}

export function isCompanionConsentAccepted(storage: CompanionConsentStorage): boolean {
  return readCompanionConsent(storage)?.choice === 'accepted';
}

export function isSiteConsentGateOpen(
  gate: CompanionSiteConsentGate | undefined = typeof window !== 'undefined'
    ? window.__camlinCompanionSiteConsent
    : undefined
): boolean {
  if (!gate) {
    return true;
  }

  try {
    return gate.isOpen();
  } catch {
    return false;
  }
}

/** True when the embed may become interactive (consent layers satisfied). */
export function isCompanionEngagementAllowed(
  storage: CompanionConsentStorage,
  gate?: CompanionSiteConsentGate
): boolean {
  return isCompanionConsentAccepted(storage) && isSiteConsentGateOpen(gate);
}

/** True when the embed must remain inert (no transcript processing, no proactive). */
export function isCompanionInert(
  storage: CompanionConsentStorage,
  gate?: CompanionSiteConsentGate
): boolean {
  return !isCompanionEngagementAllowed(storage, gate);
}

export function clearCompanionConsent(storage: CompanionConsentStorage): void {
  storage.removeItem(COMPANION_CONSENT_STORAGE_KEY);
}
